import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { ProductTndb } from "services/axiosService"
import { setCurrentProduct, setGlasses } from "store/product/productSlice"
import {
  Button,
  IconButton,
  LinkButton,
  Searchbar,
  SearchForm,
  Typography,
} from "@strapi/design-system"
import { SelectProductsEcommerceTableRow } from "./SelectProductsEcommerceTableRow"
import { Skeleton } from "@/components/ui/skeleton"
import { TinedanubeLogo } from "pages/Looks/tinedenube"
import { ProductCategories } from "@/components/Navbar/types"
import { CATEGORIES } from "pages/Products/ProductPage/mock"
import { initRenderingOptions } from "types"
import { ProductTypeName } from "store/product/types"
import { Refresh } from "@strapi/icons"
import { useTiendanubeProductsQuery } from "hooks/query/useTiendanubeQuery.ts"
import { useAllProductsQuery } from "hooks/query/useProductsQuery.ts"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

const SkeletonRow = () => (
  <TableRow>
    <TableCell className="h-8 w-16  !pl-10">
      <Skeleton width="w-11" height="h-11" />
    </TableCell>
    <TableCell className="h-8 w-36">
      <Skeleton width="w-12" height="h-4" />
    </TableCell>
    <TableCell className="h-8 w-36 justify-items-center">
      <Skeleton width="w-12" height="h-4" />
    </TableCell>
  </TableRow>
)

export const SelectProductsEcommerceStepTwo = ({ category }: { category: ProductCategories }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage] = useState(6)
  const [searchQuery, setSearchQuery] = useState("")
  const { username, merchantId } = useAppSelector((state) => state.user.userInfo)
  const dispatch = useAppDispatch()
  const { data: allProducts, isLoading: allProductsLoading } = useAllProductsQuery(merchantId)
  const {
    data: tiendanubeResponse,
    isFetching: tiendanubeLoading,
    refetch,
  } = useTiendanubeProductsQuery({
    username,
    page: currentPage,
    productName: searchQuery || undefined,
    perPage,
  })

  const tiendanubeProducts = tiendanubeResponse?.products
  const hasNext = tiendanubeResponse?.hasNext ?? false

  const tbProducts = useMemo(
    () =>
      tiendanubeProducts?.filter(
        (tbProduct) => !(allProducts || []).some((product) => product.externalId === tbProduct.id)
      ),
    [tiendanubeProducts, allProducts]
  )
  // Use products directly without category filtering
  const paginatedProducts = tbProducts || []

  // Pagination controls
  const canGoNext = hasNext
  const canGoPrevious = currentPage > 1

  const handleNext = () => {
    if (canGoNext) setCurrentPage(currentPage + 1)
  }

  const handlePrevious = () => {
    if (canGoPrevious) setCurrentPage(currentPage - 1)
  }

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  // Determine the type of empty state
  const isFirstPageEmpty =
    currentPage === 1 && !searchQuery && paginatedProducts.length === 0 && !tiendanubeLoading
  const isSearchEmpty = searchQuery && paginatedProducts.length === 0 && !tiendanubeLoading

  const selectProductHandler = (product: ProductTndb) => {
    const colors = product?.variants?.map((el) => ({
      color: "#ead9e1",
      sku: el.sku ?? "",
      upc: el.barcode ?? "",
      externalId: el.id,
      name: el.name ?? "",
    })) ?? [{ color: "#ecd6e0", sku: "", upc: "" }]

    const cover = product?.variants.length > 0 ? product?.variants[0].imageUrl : ""
    const { id, ...rest } = product
    const selectedProduct = {
      ...rest,
      externalId: id,
      cover,
      brandId: "",
      merchant: merchantId ?? "",
      url: rest.url,
      name: rest.name,
      brand: rest.brand,
      colors,
      dataFilling: true,
    }

    if (category === ProductCategories.GLASSES) {
      dispatch(
        setGlasses({
          ...selectedProduct,
          sku: product.variants[0].sku,
          categoryId: CATEGORIES?.find((el) => el.name === ProductTypeName.Glasses)?.id || 0,
          renderingOptions: initRenderingOptions,
        })
      )
      navigate("/glasses/add")
    } else {
      dispatch(setCurrentProduct(selectedProduct))
      navigate("/products/add")
    }
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])
  const isLoading = allProductsLoading || tiendanubeLoading

  // Only show the full empty state if it's the first page with no search/filters and no products
  if (!isLoading && !allProductsLoading && isFirstPageEmpty)
    return (
      <div className="flex flex-col pt-5 text-center gap-4 items-center justify-center px-6 max-w-full">
        <Typography
          as="h2"
          fontWeight="bold"
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#32324D",
            maxWidth: "100%",
            wordWrap: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {t("tinedanubeModal.noProductsInStore")}
        </Typography>
        <img src="/no-product-tinedanube.png" alt="No products" className="max-w-full h-auto" />
        <LinkButton
          variant="tertiary"
          startIcon={<TinedanubeLogo />}
          href="https://tintdemostore.mitiendanube.com/admin/v2/products"
          target="_blank"
        >
          {t("tinedanubeModal.addInTinedanube")}
        </LinkButton>
      </div>
    )
  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex gap-2 flex-col sm:flex-row justify-between">
        <SearchForm className="flex-1 hover:brightness-120">
          <Searchbar
            name="searchbar"
            onClear={() => setSearchQuery("")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            clearLabel={t("productsPage.clear")}
            placeholder={t("tinedanubeModal.searchPlaceholder")}
          />
        </SearchForm>

        <IconButton
          className="ml-auto !h-10 !w-10 !border-none  "
          onClick={() => {
            refetch()
          }}
          disabled={isLoading}
          icon={isLoading ? <Refresh className="  animate-spin" /> : <Refresh />}
        ></IconButton>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-30 !pl-10">{t("tinedanubeModal.logo")}</TableHead>
              <TableHead className="w-10">{t("tinedanubeModal.name")}</TableHead>
              <TableHead className="w-40 text-center">{t("tinedanubeModal.variants")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, index) => <SkeletonRow key={index} />)
            ) : paginatedProducts.length > 0 ? (
              paginatedProducts.map((product, ind) => (
                <SelectProductsEcommerceTableRow
                  key={ind}
                  product={product}
                  onSelect={selectProductHandler}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  {isSearchEmpty ? (
                    <div className="flex flex-col items-center space-y-3 py-12 px-8">
                      <Typography className="text-gray-600 font-medium">
                        {t("tinedanubeModal.noProductsFound", {
                          query: searchQuery,
                        })}
                      </Typography>
                      <Typography className="text-gray-400 text-sm">
                        {t("tinedanubeModal.adjustSearch")}
                      </Typography>
                    </div>
                  ) : (
                    <Typography className="text-gray-400">
                      {t("tinedanubeModal.noProducts")}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!isFirstPageEmpty && (
        <div className="flex items-center justify-between py-4 px-4 mt-4">
          <div className="text-sm text-gray-300 px-2 py-1 rounded">
            {t("tinedanubeModal.page")} {currentPage}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrevious}
              disabled={!canGoPrevious || isLoading}
              variant="tertiary"
              size="S"
            >
              {"<"}
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canGoNext || isLoading}
              variant="tertiary"
              size="S"
            >
              {">"}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
